import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../../hooks/useAuth';
import { useEnv } from '../../../../context/env-context';
import { makeRequest } from '../../../../utils/makeRequest';
import { toast } from 'react-toastify';
import { Spinner } from '../../../../components/Spinner';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRotateRight } from '@fortawesome/free-solid-svg-icons';
import { CircularProgress } from '@mui/material';
import CollapsedPanel from '../../../../components/CollapsedPanel';
// common
import CommonAccordion from '../../../../components/common/CommonAccordion';
import CommonSelectInput from '../../../../components/common/CommonSelectInput';
import CommonTextInput from '../../../../components/common/CommonTextInput';
import CommonMultiLineTextInput from '../../../../components/common/CommonMultiLineTextInput';
import CommonDefaultTextInput from '../../../../components/common/CommonDefaultTextInput';
// forms
import ConsumerPopoverNameForm from './forms/ConsumerPopoverNameForm';
import ConsumerPopoverGhostForm from './forms/ConsumerPopoverGhostForm';
import ConsumerPopoverAuthenticateForm from './forms/ConsumerPopoverAuthenticateForm';
import ConsumerPopoverStatusForm from './forms/ConsumerPopoverStatusForm';
import ConsumerPopoverSearchAggregatorForm from './forms/ConsumerPopoverSearchAggregatorForm';
import ConsumerPopoverPhoneNumberForm from './forms/ConsumerPopoverPhoneNumberForm';
import ConsumerPopoverAddressForm from './forms/ConsumerPopoverAddressForm';
import ConsumerPopoverDateOfBirthForm from './forms/ConsumerPopoverDateOfBirthForm';
import ConsumerGhostingForm from './forms/ConsumerGhostingForm';
import ConsumerPopoverTerminationDate from './forms/ConsumerPopoverTerminationDate';
import ConsumerFinchEmployeeIdForm from './forms/ConsumerFinchEmployeeIdForm';
// widgets
import ConsumerRedFlagsWidget from './widgets/ConsumerRedFlagsWidget';
import ConsumerGhostedByWidget from './widgets/ConsumerGhostedByWidget';
import ConsumerBridgePointsWidget from './widgets/ConsumerBridgePointsWidget';
import ConsumerOpenFundingWidget from './widgets/ConsumerOpenFundingWidget';
import ConsumerActiveOfferWidget from './widgets/ConsumerActiveOfferWidget';
import ConsumerClosedFundingsWidget from './widgets/ConsumerClosedFundingsWidget';
// modals
import ConsumerEmployeeViewModal from './modals/ConsumerEmployeeViewModal';
import ConsumerMatchCandidatesModal from './modals/ConsumerMatchCandidatesModal';
// assests
import androidImage from '../../../../assets/mobile-icons/android.png';
import iosImage from '../../../../assets/mobile-icons/ios.png';

function ConsumerDetailsPanel({
  collapsedPanels,
  expandParentPanel,
  loading,
  setJobId,
  jobStatus,
  refreshConsumer,
  consumer,
  setConsumer,
  aggregator,
  aggregators,
  bridgePointsBalance,
  openFunding,
  closedFundings,
  profileJson,
  mobileDevice,
  panelDataRefresh,
  setPanelDataRefresh,
}) {
  const [spinner, setSpinner] = useState(false);

  const [employeeView, setEmployeeView] = useState(null);
  const [finchMatchCandidates, setFinchMatchCandidates] = useState(null);

  const { apiOriginConsumer } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const refresh = useCallback(async () => {
    setSpinner(true);
    await refreshConsumer(consumer.id);
    setSpinner(false);
  }, [refreshConsumer, consumer?.id]);

  useEffect(() => {
    if (panelDataRefresh.consumer) {
      refresh();
      setPanelDataRefresh(prev => ({ ...prev, consumer: false }));
    }
  }, [panelDataRefresh, refresh, setPanelDataRefresh]);

  const resetReferral = async () => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }
    const config = {
      token,
      url: `${apiOriginConsumer}/reset_referral`,
      method: 'POST',
      data: JSON.stringify({ consumerId: consumer.id }),
    };

    try {
      await makeRequest(config);
      setConsumer(prevConsumer => ({ ...prevConsumer, referringConsumerId: '' }));
      toast.success('Referring consumer has been reset!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const setReferral = async value => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }
    const config = {
      token,
      url: `${apiOriginConsumer}/set_referral`,
      method: 'POST',
      data: JSON.stringify({ consumerId: consumer.id, referringConsumerId: value }),
    };

    try {
      await makeRequest(config);
      setConsumer(prevConsumer => ({ ...prevConsumer, referringConsumerId: value }));
      toast.success('Referring consumer has been updated!');
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  const updateConsumer = useCallback(
    async (fields, sendRefreshRequest) => {
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginConsumer}/update_consumer`,
        method: 'POST',
        data: JSON.stringify({ consumerId: consumer.id, ...fields }),
      };

      try {
        const response = await makeRequest(config);

        if (response.status === 'ok') {
          setConsumer(prevConsumer => ({ ...prevConsumer, ...fields }));
        }

        if (response.jobId !== null) {
          setJobId(response.jobId);
        }

        if (sendRefreshRequest) {
          refresh();
        }
      } catch (error) {
        toast.error(error.message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAccessTokenSilently, apiOriginConsumer, refresh],
  );

  const createHubSpotContanct = async () => {
    if (window.confirm('Are you sure?')) {
      setSpinner(true);
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginConsumer}/hubspot/create_contact`,
        method: 'POST',
        data: JSON.stringify({ consumerId: consumer.id }),
      };

      try {
        await makeRequest(config);

        toast.success('HubSpot Contact successfully updated!');
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSpinner(false);
      }
    }
  };

  const moveToGeneric = async () => {
    if (window.confirm('Are you sure?')) {
      setSpinner(true);
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginConsumer}/retro_change_to_generic`,
        method: 'POST',
        data: JSON.stringify({ consumerId: consumer.id }),
      };

      try {
        await makeRequest(config);
        await refresh();

        toast.success('Successfully moved consumer to generic aggregator!');
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSpinner(false);
      }
    }
  };

  if (collapsedPanels.details) {
    return <CollapsedPanel side="right" onClick={() => expandParentPanel(0)} />;
  }

  return (
    <>
      <Spinner visible={spinner}>
        <div className="h-100">
          <div className="py-2 px-3">
            <div className="d-flex justify-content-between align-items-center">
              <ConsumerPopoverNameForm
                consumerIsNew={consumer.isNew}
                consumerFirstName={consumer.firstName}
                consumerLastName={consumer.lastName}
                updateConsumer={updateConsumer}
              />
              <div className="d-flex align-items-center">
                <ConsumerPopoverStatusForm
                  consumerStatus={consumer.status}
                  updateConsumer={updateConsumer}
                />
                <Button className="bo-common-base-button pb-1" onClick={refresh}>
                  <FontAwesomeIcon icon={faArrowRotateRight} size="sm" color="#5946A4" />
                </Button>
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center pb-1">
              <p className="bo-common-text-bold p-0 m-0">{`ID: ${consumer.id}`}</p>
              <div className="d-flex gap-3">
                <ConsumerPopoverGhostForm consumerId={consumer.id} refresh={refresh} />
                <ConsumerPopoverAuthenticateForm
                  consumerId={consumer.id}
                  consumerMfaChannel={consumer.mfaChannel}
                />
              </div>
            </div>
            <ConsumerPopoverSearchAggregatorForm
              consumerAggregatorId={consumer.aggregatorId}
              updateConsumer={aggregatorId => updateConsumer({ aggregatorId }, true)}
              aggregators={aggregators}
            />
            <p className="bo-common-text p-0 m-0 pb-1">{consumer.email}</p>
            <div className="my-2 d-flex flex-column align-items-center gap-2">
              {consumer.flags.length > 0 && (
                <ConsumerRedFlagsWidget consumerFlags={consumer.flags} />
              )}
              {consumer.ghostingConsumers.length > 0 && (
                <ConsumerGhostedByWidget
                  consumerGhostingConsumers={consumer.ghostingConsumers}
                  setConsumer={setConsumer}
                />
              )}
              <ConsumerBridgePointsWidget
                loading={loading}
                bridgePointsBalance={bridgePointsBalance}
              />
              {openFunding && openFunding.id && (
                <ConsumerOpenFundingWidget
                  openFunding={openFunding}
                  consumerBankAccountStatus={consumer.bankAccountStatus}
                />
              )}
              <ConsumerActiveOfferWidget
                loading={loading}
                jobStatus={jobStatus}
                setJobId={setJobId}
                refreshConsumer={refresh}
                consumerId={consumer.id}
                consumerAdvanceEligibility={consumer.advanceEligibility}
                updateConsumer={updateConsumer}
                openFunding={openFunding}
                profileJson={profileJson}
              />
              {closedFundings && closedFundings.length > 0 && (
                <ConsumerClosedFundingsWidget
                  closedFundings={closedFundings}
                  consumerId={consumer.id}
                  consumerFirstName={consumer.firstName}
                  consumerLastName={consumer.lastName}
                />
              )}
            </div>
          </div>
          <CommonAccordion title="Personal and Contact Details">
            <ConsumerPopoverPhoneNumberForm
              consumerId={consumer.id}
              consumerPhoneCc={consumer.phoneCc}
              consumerPhoneNdc={consumer.phoneNdc}
              consumerPhoneSn={consumer.phoneSn}
              consumerIsPhoneVerified={consumer.isPhoneVerified}
              setConsumer={setConsumer}
            />
            <ConsumerPopoverAddressForm
              consumerAddressLine1={consumer.addressLine1}
              consumerAddressLine2={consumer.addressLine2}
              consumerCity={consumer.city}
              consumerState={consumer.state}
              consumerZip={consumer.zip}
              updateConsumer={updateConsumer}
            />
            <ConsumerPopoverDateOfBirthForm
              consumerDateOfBirth={consumer.dateOfBirth}
              updateConsumer={updateConsumer}
            />
            <CommonTextInput
              label="Last 4 SSN digits"
              initialValue={consumer.last4ssn}
              labelClassName="mt-3"
              maxLength={4}
              type="numeric"
              allowLeadingZeros
              onSave={value => updateConsumer({ last4ssn: value })}
            />
            <CommonSelectInput
              label="Language"
              initialValue={consumer.language}
              labelClassName="mt-3"
              options={[
                { key: 'en', value: 'en', name: 'English' },
                { key: 'es', value: 'es', name: 'Spanish' },
              ]}
              onSave={value => updateConsumer({ language: value })}
            />
            <Form.Label className="bo-common-input-label mt-3">Mobile Device</Form.Label>
            {loading.mobileDevice ? (
              <div className="h-100 text-dark">
                <CircularProgress color="inherit" size={16} />
              </div>
            ) : (
              <div className="bo-consumer-mobile-platform">
                {mobileDevice && mobileDevice === 'android' && (
                  <>
                    <img alt="ios" src={androidImage} width="auto" height="20" />
                    <p className="bo-common-text-bold align-self-end">Android</p>
                  </>
                )}
                {mobileDevice && mobileDevice === 'ios' && (
                  <>
                    <img alt="ios" src={iosImage} width="auto" height="20" />
                    <p className="bo-common-text-bold align-self-end">iOS</p>
                  </>
                )}
                {!mobileDevice && <p className="bo-common-text-bold align-self-end">-----</p>}
              </div>
            )}
          </CommonAccordion>
          <CommonAccordion
            title="Employment Details"
            renderRightPart={isOpen =>
              aggregator &&
              aggregator.finchConnections.length > 0 && (
                <>
                  {!consumer.finchEmployeeId && !consumer.finchMatchCandidates && (
                    <Badge className="ms-auto bo-common-inline-badge top-0" bg="danger">
                      Not Linked
                    </Badge>
                  )}
                  {consumer.finchEmployeeId && !consumer.finchMatchCandidates && (
                    <Badge className="ms-auto bo-common-inline-badge top-0" bg="success">
                      Linked
                    </Badge>
                  )}
                  {!consumer.finchEmployeeId && consumer.finchMatchCandidates && (
                    <div className="ms-auto position-relative">
                      <Badge className="bo-common-inline-badge" bg="warning">
                        Matching Candidates
                      </Badge>
                      {isOpen && (
                        <div className="position-absolute end-0">
                          {/* eslint-disable */}
                            <div
                              className="bo-common-inline-button bo-common-text-sm px-1"
                              onClick={() =>
                                setFinchMatchCandidates(
                                  consumer.finchMatchCandidates
                                )
                              }
                            >
                              Show Candidates
                            </div>
                            {/* eslint-enable */}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )
            }
          >
            <ConsumerPopoverSearchAggregatorForm
              consumerAggregatorId={consumer.aggregatorId}
              updateConsumer={aggregatorId => updateConsumer({ aggregatorId }, true)}
              aggregators={aggregators}
              showInInputFormat
            />
            <CommonTextInput
              label="Employee ID"
              initialValue={consumer.employeeId}
              labelClassName="mt-3"
              onSave={value => updateConsumer({ employeeId: value })}
            />
            {aggregator && consumer.finchEmployeeId && (
              <>
                <ConsumerFinchEmployeeIdForm
                  consumerId={consumer.id}
                  consumerFinchConnectionId={consumer.finchConnectionId}
                  consumerFinchEmployeeId={consumer.finchEmployeeId}
                  setConsumer={setConsumer}
                  setEmployeeView={setEmployeeView}
                />
                <CommonTextInput
                  label="Finch Connection"
                  initialValue={
                    aggregator.finchConnections?.find(
                      x => x.connectionId === consumer.finchConnectionId,
                    )?.companyName || consumer.finchConnectionId
                  }
                  labelClassName="mt-3"
                  readOnly
                />
              </>
            )}
            <ConsumerPopoverTerminationDate
              consumerLastEmploymentTerminationDate={consumer.lastEmploymentTerminationDate}
              updateConsumer={updateConsumer}
            />
          </CommonAccordion>
          <CommonAccordion title="Bridgeover Service">
            <div className="d-flex gap-2 align-items-center">
              <Button className="bo-common-button px-2" onClick={createHubSpotContanct}>
                <p className="bo-common-text-sm">Create/Update HubSpot Contact</p>
              </Button>
              {moment().diff(moment.utc(consumer.createDate).local(), 'days') <= 30 && (
                <Button className="bo-common-button px-2" onClick={moveToGeneric}>
                  <p className="bo-common-text-sm">Move to Generic</p>
                </Button>
              )}
            </div>
            <CommonTextInput
              label="Registration Time"
              readOnly
              labelClassName="mt-3"
              initialValue={moment
                .utc(consumer.createDate)
                .local()
                .format('MMM DD, YYYY HH:mm (UTCZ)')}
            />
            <CommonTextInput
              label="Referred By"
              initialValue={consumer.referringConsumerId}
              labelClassName="mt-3"
              type="numeric"
              onSave={value => setReferral(value)}
              onDelete={() => resetReferral()}
            />
            <CommonTextInput
              label="MFA Channel"
              readOnly
              labelClassName="mt-3"
              initialValue={consumer.mfaChannel}
            />
            <CommonSelectInput
              label="Default Payment Method"
              initialValue={consumer.defaultPaymentMethod}
              labelClassName="mt-3"
              options={[
                { key: 'ACH', value: 'ACH', name: 'ACH' },
                { key: 'CARD', value: 'CARD', name: 'CARD' },
              ]}
              onSave={value => updateConsumer({ defaultPaymentMethod: value })}
            />
            <CommonSelectInput
              label="Card Required For Taking an Advance"
              initialValue={consumer.cardRequired ? 'Yes' : 'No'}
              labelClassName="mt-3"
              options={[
                { key: 'Yes', value: 'Yes', name: 'Yes' },
                { key: 'No', value: 'No', name: 'No' },
              ]}
              onSave={value => updateConsumer({ cardRequired: value === 'Yes' })}
            />
            <CommonSelectInput
              label="Allow Credit Card"
              initialValue={consumer.allowCreditCard ? 'Yes' : 'No'}
              labelClassName="mt-3"
              options={[
                { key: 'Yes', value: 'Yes', name: 'Yes' },
                { key: 'No', value: 'No', name: 'No' },
              ]}
              onSave={value => updateConsumer({ allowCreditCard: value === 'Yes' })}
            />
            <CommonMultiLineTextInput
              label="Notes"
              labelClassName="mt-3"
              initialValue={consumer.notes}
              onSave={value => updateConsumer({ notes: value })}
            />
            <CommonSelectInput
              label="High Risk"
              initialValue={consumer.highRisk ? 'Yes' : 'No'}
              labelClassName="mt-3"
              options={[
                { key: 'Yes', value: 'Yes', name: 'Yes' },
                { key: 'No', value: 'No', name: 'No' },
              ]}
              onSave={value => updateConsumer({ highRisk: value === 'Yes' }, true)}
            />
            {consumer.highRisk && (
              <CommonMultiLineTextInput
                label="High Risk Reason"
                labelClassName="mt-3"
                initialValue={consumer.highRiskReason}
                onSave={value => updateConsumer({ highRiskReason: value }, true)}
              />
            )}
            <CommonSelectInput
              label="Avoid Billing"
              initialValue={consumer.avoidBilling ? 'Yes' : 'No'}
              labelClassName="mt-3"
              options={[
                { key: 'Yes', value: 'Yes', name: 'Yes' },
                { key: 'No', value: 'No', name: 'No' },
              ]}
              onSave={value => updateConsumer({ avoidBilling: value === 'Yes' })}
            />
            <CommonSelectInput
              label="New Consumer"
              initialValue={consumer.isNew ? 'Yes' : 'No'}
              labelClassName="mt-3"
              options={[
                { key: 'Yes', value: 'Yes', name: 'Yes' },
                { key: 'No', value: 'No', name: 'No' },
              ]}
              onSave={value => updateConsumer({ isNew: value === 'Yes' })}
            />
          </CommonAccordion>
          {consumer.ghostingUser && (
            <CommonAccordion
              title="Ghosting"
              renderRightPart={() => (
                <Badge
                  className="ms-auto bo-common-inline-badge top-0"
                  bg={consumer.ghostedConsumerId ? 'danger' : 'secondary'}
                >
                  {consumer.ghostedConsumerId ? 'Ghosting' : 'Not Ghosting'}
                </Badge>
              )}
            >
              <ConsumerGhostingForm
                consumerId={consumer?.id}
                consumerGhostedConsumerId={consumer?.ghostedConsumerId}
                setConsumer={setConsumer}
              />
            </CommonAccordion>
          )}
          <CommonAccordion title="Settings" bottomBorder>
            <CommonDefaultTextInput
              label="Free Credit Transfers"
              initialValue={consumer.defaultFreeCreditTransfers}
              onSave={value => updateConsumer({ defaultFreeCreditTransfers: value })}
            />
            <CommonDefaultTextInput
              label="Free Debit Transfers"
              labelClassName="mt-3"
              initialValue={consumer.defaultFreeDebitTransfers}
              onSave={value => updateConsumer({ defaultFreeDebitTransfers: value })}
            />
            <CommonDefaultTextInput
              label="Transfer Fee ($)"
              labelClassName="mt-3"
              initialValue={consumer.defaultTransferFee}
              decimalScale={2}
              onSave={value => updateConsumer({ defaultTransferFee: value })}
            />
            <CommonDefaultTextInput
              label="Monthly BridgePoints™ Grant"
              labelClassName="mt-3"
              initialValue={consumer.bpMonthlyQuota}
              onSave={value => updateConsumer({ bpMonthlyQuota: value })}
            />
            <CommonDefaultTextInput
              label="BridgePoints™ Cap"
              labelClassName="mt-3"
              initialValue={consumer.bpCap}
              onSave={value => updateConsumer({ bpCap: value })}
            />
            <CommonTextInput
              label="Max Advance ($)"
              initialValue={consumer.advanceLimit}
              labelClassName="mt-3"
              type="numeric"
              allowLeadingZeros={false}
              onSave={value => updateConsumer({ advanceLimit: value })}
              allowEmpty={false}
            />
            <CommonDefaultTextInput
              label="Min Payroll Cycles"
              labelClassName="mt-3"
              initialValue={consumer.minPayrollCycles}
              onSave={value => updateConsumer({ minPayrollCycles: value })}
            />
            <CommonDefaultTextInput
              label="Min Employment Period (weeks)"
              labelClassName="mt-3"
              initialValue={consumer.minPayrollWeeks}
              onSave={value => updateConsumer({ minPayrollWeeks: value })}
            />
            <CommonDefaultTextInput
              label="Min Account History (days)"
              labelClassName="mt-3"
              initialValue={consumer.minAccountDays}
              onSave={value => updateConsumer({ minAccountDays: value })}
            />
            <CommonDefaultTextInput
              label="Pre Payday Cluster Extension"
              labelClassName="mt-3"
              initialValue={consumer.prePaydayClusterExtension}
              onSave={value => updateConsumer({ prePaydayClusterExtension: value })}
            />
            <CommonDefaultTextInput
              label="Post Payday Cluster Extension"
              labelClassName="mt-3"
              initialValue={consumer.postPaydayClusterExtension}
              onSave={value => updateConsumer({ postPaydayClusterExtension: value })}
            />
          </CommonAccordion>
        </div>
      </Spinner>
      <ConsumerEmployeeViewModal
        employeeView={employeeView}
        setEmployeeView={setEmployeeView}
        closeModal={() => setEmployeeView(null)}
      />
      <ConsumerMatchCandidatesModal
        consumerId={consumer.id}
        refreshConsumer={refreshConsumer}
        finchMatchCandidates={finchMatchCandidates}
        setEmployeeView={setEmployeeView}
        closeModal={() => setFinchMatchCandidates(null)}
      />
    </>
  );
}

export default ConsumerDetailsPanel;
